import { useMutation } from "@apollo/client";
import { Alert, Button, Stack, TextField, Typography } from "@mui/material";
import React, { useState } from "react";

import { describeError } from "../../common/rateLimit";
import { SEND_WHATSAPP_TEST_MESSAGE } from "./whatsapp.mutation";
import { parsePhones } from "./whatsapp";

const MAX_TEXT = 1600;

interface TestMessageResult {
  sid: string;
  sender: string;
  status: string;
  delivered: boolean;
  errorCode: number | null;
  explanation: string;
}

/**
 * Sends one real WhatsApp message to one number, on every server, development
 * included, and shows what became of it. The API waits up to 20 seconds for
 * Twilio's answer, because Twilio accepts a message first and fails it later.
 *
 * Free text: it reaches a phone that joined the Twilio Sandbox, or one that
 * messaged our sender in the last 24 hours. Before a sender is set up it comes
 * from the Sandbox.
 */
export const WhatsAppTestMessage: React.FC<{ testSender?: string }> = ({ testSender }) => {
  const [phone, setPhone] = useState("");
  const [text, setText] = useState("");
  const [send, { data, loading, error, reset }] = useMutation(SEND_WHATSAPP_TEST_MESSAGE, {
    errorPolicy: "none",
    // Shown inline below rather than as a toast, next to the answer it replaces.
    onError: () => undefined,
  });
  const result: TestMessageResult | undefined = data?.sendWhatsAppTestMessage;

  const { phones, invalid } = parsePhones(phone);
  const phoneProblem =
    phones.length > 1 ? "One number." : invalid.length ? "International format, e.g. +12025550143." : "";
  const textProblem = text.length > MAX_TEXT ? `Up to ${MAX_TEXT} characters.` : "";
  const canSend = phones.length === 1 && !phoneProblem && !!text.trim() && !textProblem && !loading;

  const onSend = () => {
    reset();
    send({ variables: { input: { phone: phones[0], text: text.trim() } } });
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h6">Send test WhatsApp message</Typography>
      <Typography variant="body2" color="text.secondary">
        A real message to one number, on every server{testSender ? `, from ${testSender}` : ""}. It reaches a
        phone that has joined the Twilio Sandbox, or one that messaged this sender in the last 24 hours.
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
        <TextField
          label="Number"
          placeholder="+12025550143"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          error={!!phoneProblem}
          helperText={phoneProblem || " "}
          sx={{ minWidth: 220 }}
        />
        <TextField
          label="Message"
          value={text}
          onChange={(e) => setText(e.target.value)}
          error={!!textProblem}
          helperText={textProblem || " "}
          multiline
          minRows={1}
          sx={{ flexGrow: 1 }}
        />
        <Button variant="contained" onClick={onSend} disabled={!canSend} sx={{ mt: 1 }}>
          {loading ? "Sending… (up to 20s)" : "Send test WhatsApp message"}
        </Button>
      </Stack>

      {error && <Alert severity="error">{describeError(error, "Not sent")}</Alert>}
      {result && (
        <Alert
          data-testid="whatsapp-test-result"
          severity={result.delivered ? "success" : result.status === "failed" || result.status === "undelivered" ? "error" : "info"}
        >
          {result.explanation}
          <Typography variant="caption" component="div">
            Twilio status: {result.status}
            {result.errorCode ? ` (error ${result.errorCode})` : ""} · from {result.sender} · {result.sid}
          </Typography>
        </Alert>
      )}
    </Stack>
  );
};
